import React from 'react';

export interface NumberChoiceProps<TValue extends number> {
  label: string;
  values: readonly TValue[];
  value: TValue;
  onChange: (value: TValue) => void;
  /** How a value reads on its chip; defaults to the number itself. */
  format?: (value: TValue) => string;
  hint?: string;
}

/**
 * Pill picker over a small set of numbers, where the value itself is the label:
 * groups, DALI masters, channel numbers. Same treatment as `SegmentedChoice`, so
 * a technician sees one kind of selector everywhere.
 */
export function NumberChoice<TValue extends number>({
  label,
  values,
  value,
  onChange,
  format,
  hint,
}: NumberChoiceProps<TValue>): React.ReactElement {
  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-[#333333]">{label}</span>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
        {values.map((entry) => {
          const isSelected = entry === value;
          return (
            <button
              key={entry}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onChange(entry)}
              className={`min-w-[2.75rem] rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                isSelected
                  ? 'border-[#D4A054] bg-[#FDF6E9] text-[#333333]'
                  : 'border-[#E0E0E0] bg-white text-[#666666] hover:border-[#D4A054]/60'
              }`}
            >
              {format === undefined ? entry : format(entry)}
            </button>
          );
        })}
      </div>
      {hint !== undefined && <span className="mt-1.5 block text-xs text-[#666666]">{hint}</span>}
    </div>
  );
}

export default NumberChoice;
